import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Button from "../components/Button";
import InputField from "../components/InputField";
import Profile from "../components/Profile";
import api from "../api";
import { useAuth } from "../hook/useAuth";
import "./AddDetails.css";

interface FormData {
  name: string;
  profileImage: string;
}

const AddDetails: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { validateSession } = useAuth();
  const [formData, setFormData] = useState<FormData>({
    name: "",
    profileImage: "",
  });
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userId, setUserId] = useState<number | null>(null);
  const [existingProfileImage, setExistingProfileImage] = useState<
    string | null
  >(null);

  const fromOtp = location.state?.fromOtp || false;
  const isEditMode = location.state?.isEditMode || false;

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setFetching(true);
        const response = await api.getProfile();
        console.log("Profile response:", response);

        setUserId(Number(response.id));

        // If user has name, pre-fill it (handle null case)
        if (response.name && response.name.trim() !== "") {
          setFormData((prev) => ({ ...prev, name: response.name || "" }));
        }

        // If user has profile picture, pre-fill it
        if (response.profilePicture) {
          const imageUrl = `https://api.flooron.in/${response.profilePicture}`;
          setExistingProfileImage(imageUrl);
          setFormData((prev) => ({ ...prev, profileImage: imageUrl }));
        }

        if (response.phoneNumber) {
          console.log("Phone number:", response.phoneNumber);
        }
      } catch (err) {
        console.error("Error fetching user profile:", err);
        setError("Failed to load user data. Please try again.");
      } finally {
        setFetching(false);
      }
    };

    fetchUserProfile();
  }, []);
  // Handle navigation after data is loaded
  useEffect(() => {
    if (!fetching && userId) {
      // If in edit mode (from profile), always stay on this page
      if (isEditMode) {
        console.log("Edit mode: staying on add details page");
        return;
      }

      // If coming from OTP, always stay on this page
      if (fromOtp) {
        console.log("Coming from OTP flow, staying on add details page");
        return;
      }

      // If not from OTP and user has name, go back to previous page
      if (formData.name.trim() !== "") {
        console.log("User has name, navigating back");
        navigate(-1);
      }
    }
  }, [fetching, userId, formData.name, fromOtp, isEditMode, navigate]);

  const handleChange = (field: keyof FormData) => (value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (error) setError(null);
  };

  const handleProfileImageSelect = (file: File) => {
    setProfileImageFile(file);
    const imageUrl = URL.createObjectURL(file);
    setFormData((prev) => ({ ...prev, profileImage: imageUrl }));
  };

  const showError = (message: string) => {
    setError(message);
    console.error("Error", message);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      showError("Please enter your name");
      return;
    }

    if (!userId) {
      showError("User ID not found. Please login again.");
      return;
    }

    try {
      setLoading(true);

      // Create FormData to send both file and JSON data together
      const formDataToSend = new FormData();

      // Append the file if selected
      if (profileImageFile) {
        setUploading(true);
        formDataToSend.append("profilePicture", profileImageFile);
      }

      // Append the name
      formDataToSend.append("name", formData.name.trim());

      // Make single API call with FormData
      const res = await api.saveUserDetails(userId, formDataToSend);
      console.log("User details saved:", res);

      const sessionValid = await validateSession();
      if (!sessionValid) {
        showError("Session expired. Please login again.");
        return;
      }

      navigate("/");
    } catch (err: any) {
      console.error("Full error:", err);
      let errorMessage =
        err?.message || "Failed to save user details. Please try again.";
      if (err?.message) {
        if (err.message.includes("413") || err.message.includes("too large")) {
          errorMessage =
            "Image file is too large. Please select a smaller image.";
        } else if (err.message.includes("Authentication")) {
          errorMessage = "Session expired. Please login again.";
        }
      }
      showError(errorMessage);
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  // Show loading state while fetching user data
  if (fetching) {
    return (
      <div className="login-container">
        <div className="login-form-wrapper">
          <div className="loading-spinner">
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-form-wrapper">
        <h2 className="form-main-title">Complete Your Profile</h2>

        <div className="profile-image-section">
          <Profile
            onImageSelect={handleProfileImageSelect}
            currentImage={formData.profileImage || undefined}
            size="small"
          />
          <p>Select Profile Picture</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form
          className="expert-form-stack"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <InputField
            label="Full Name"
            value={formData.name}
            onChange={handleChange("name")}
          />

          <Button
            type="submit"
            variant="primary"
            className="full-width-btn"
            disabled={loading || uploading}
          >
            {loading ? "Saving..." : "Save Details"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default AddDetails;
